import { Recipe } from '@api/Recipes/Recipe';
import { ModifierGroup, ModifierGroupOption } from '@api/ModifierGroups/ModifierGroup';
import { Dish } from '@api/Dishes/Dish';

// ============================================================================
// Disponibilidad derivada del catálogo 2.0 a partir de los AGOTADOS por sucursal
// (`branch_offices.unavailableInputs` — ver §4.3 del plan de reestructura).
//
// Regla: insumo agotado → receta que lo usa BLOQUEADA (también si el item es
// `optional`: la preparación default lo incluye; el staff decide con el preview
// de impacto) → se ocultan productos con componente bloqueado, opciones de
// modificador con receta bloqueada, y productos cuyo grupo OBLIGATORIO
// (required o min > 0) se queda sin opciones disponibles.
//
// ⚠️ PARIDAD CROSS-REPO: krispis-web replica este módulo en F3 para filtrar el
// menú del cliente (misma regla que PointsHelper) — cambiarlo aquí exige
// cambiarlo allá.
// ============================================================================

export interface CatalogSnapshot {
    recipes: Recipe[];
    modifierGroups: ModifierGroup[];
    dishes: Dish[];
}

export interface CatalogAvailability {
    unavailableRecipeIds: Set<string>;
    unavailableDishIds: Set<string>;
    // groupId → ids de opciones bloqueadas dentro del grupo
    unavailableOptionIdsByGroup: Map<string, Set<string>>;
}

export const getUnavailableRecipeIds = (recipes: Recipe[], unavailableInputIds: Set<string>): Set<string> => {
    const unavailableRecipeIds = new Set<string>();
    if (unavailableInputIds.size === 0) {
        return unavailableRecipeIds;
    }
    recipes.forEach((recipe) => {
        if (recipe.id && (recipe.items || []).some((item) => unavailableInputIds.has(item.inputId))) {
            unavailableRecipeIds.add(recipe.id);
        }
    });
    return unavailableRecipeIds;
};

const isOptionBlocked = (option: ModifierGroupOption, unavailableRecipeIds: Set<string>): boolean =>
    unavailableRecipeIds.has(option.recipeId);

// Un grupo "se queda sin opciones" cuando ninguna opción ACTIVA tiene receta disponible
const groupHasNoAvailableOptions = (group: ModifierGroup, unavailableRecipeIds: Set<string>): boolean => {
    const activeOptions = (group.options || []).filter((option) => option.status);
    if (activeOptions.length === 0) {
        return false; // grupo vacío/inactivo: no bloquea (no hay nada que elegir de origen)
    }
    return activeOptions.every((option) => isOptionBlocked(option, unavailableRecipeIds));
};

export const getCatalogAvailability = (
    catalog: CatalogSnapshot,
    unavailableInputIds: Set<string>
): CatalogAvailability => {
    const unavailableRecipeIds = getUnavailableRecipeIds(catalog.recipes, unavailableInputIds);

    const unavailableOptionIdsByGroup = new Map<string, Set<string>>();
    const groupsById = new Map<string, ModifierGroup>();
    catalog.modifierGroups.forEach((group) => {
        if (!group.id) return;
        groupsById.set(group.id, group);
        const blockedOptions = new Set(
            (group.options || [])
                .filter((option) => isOptionBlocked(option, unavailableRecipeIds))
                .map((option) => option.id)
        );
        if (blockedOptions.size > 0) {
            unavailableOptionIdsByGroup.set(group.id, blockedOptions);
        }
    });

    const unavailableDishIds = new Set<string>();
    catalog.dishes.forEach((dish) => {
        if (!dish.id) return;
        const componentBlocked = (dish.components || []).some((component) =>
            unavailableRecipeIds.has(component.recipeId)
        );
        const requiredGroupBlocked = (dish.modifierGroups || []).some((dishGroup) => {
            const isRequired = dishGroup.required || dishGroup.min > 0;
            if (!isRequired) return false;
            const group = groupsById.get(dishGroup.groupId);
            if (!group) return false; // referencia rota: no bloquear por agotados (la UI ya la marca en rojo)
            return groupHasNoAvailableOptions(group, unavailableRecipeIds);
        });
        if (componentBlocked || requiredGroupBlocked) {
            unavailableDishIds.add(dish.id);
        }
    });

    return { unavailableRecipeIds, unavailableDishIds, unavailableOptionIdsByGroup };
};

// Impacto de marcar UN insumo como agotado (para el preview de confirmación en admin):
// qué recetas lo usan y qué productos/opciones desaparecerían del menú por SOLO este insumo.
export interface InputImpact {
    recipes: Recipe[];
    dishes: Dish[];
    options: { group: ModifierGroup; option: ModifierGroupOption }[];
}

export const getInputImpact = (inputId: string, catalog: CatalogSnapshot): InputImpact => {
    const availability = getCatalogAvailability(catalog, new Set([inputId]));
    const recipes = catalog.recipes.filter((recipe) => recipe.id && availability.unavailableRecipeIds.has(recipe.id));
    const dishes = catalog.dishes.filter((dish) => dish.id && availability.unavailableDishIds.has(dish.id));
    const options: InputImpact['options'] = [];
    catalog.modifierGroups.forEach((group) => {
        if (!group.id) return;
        const blockedOptions = availability.unavailableOptionIdsByGroup.get(group.id);
        if (!blockedOptions) return;
        (group.options || []).forEach((option) => {
            if (blockedOptions.has(option.id)) {
                options.push({ group, option });
            }
        });
    });
    return { recipes, dishes, options };
};
